import type { CaptionAnimationStyle, Subtitle, SubtitleWord } from "../types/timeline";
import type { TextClip, TextStyle } from "./types";
import { renderAnimatedCaption, type WordSegment } from "./caption-animation-renderer";

/**
 * Draws generated captions with per-word animation.
 *
 * Text clips are rendered by one shared 2D path (`titleEngine.renderText`) in
 * both preview and export, so putting caption painting here means karaoke that
 * looks right while scrubbing also burns into the exported file. There is no
 * second implementation to drift.
 */

export interface CaptionClipData {
  readonly words: readonly SubtitleWord[];
  readonly animationStyle: CaptionAnimationStyle;
  readonly highlightColor?: string;
  /** Wrap width as a fraction of frame width; defaults to 0.8. */
  readonly maxWidthRatio?: number;
}

type Ctx = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

/**
 * Reads caption data off a text clip, or null when the clip is ordinary text.
 * Static captions return null too: nothing about them needs this path.
 */
export function getCaptionClipData(clip: TextClip): CaptionClipData | null {
  const meta = clip.metadata;
  if (!meta?.captionWords?.length) return null;

  const animationStyle = meta.captionAnimationStyle ?? "none";
  if (animationStyle === "none") return null;

  return {
    words: meta.captionWords,
    animationStyle,
    highlightColor: meta.captionHighlightColor,
    maxWidthRatio:
      typeof meta.captionMaxWidthRatio === "number"
        ? meta.captionMaxWidthRatio
        : undefined,
  };
}

interface LaidOutLine {
  readonly segments: WordSegment[];
  readonly width: number;
}

/**
 * Greedy word wrap. Measures with the caption's own font so the result matches
 * what actually gets painted.
 */
function layoutLines(
  ctx: Ctx,
  segments: WordSegment[],
  maxWidth: number,
): LaidOutLine[] {
  const spaceWidth = ctx.measureText(" ").width;
  const lines: LaidOutLine[] = [];
  let current: WordSegment[] = [];
  let width = 0;

  for (const segment of segments) {
    const segmentWidth = ctx.measureText(segment.text).width;
    const added = current.length === 0 ? segmentWidth : spaceWidth + segmentWidth;

    if (current.length > 0 && width + added > maxWidth) {
      lines.push({ segments: current, width });
      current = [segment];
      width = segmentWidth;
      continue;
    }

    current.push(segment);
    width += added;
  }

  if (current.length > 0) {
    lines.push({ segments: current, width });
  }

  return lines;
}

/**
 * The karaoke animation describes a half-filled word as a CSS
 * `linear-gradient(...)` string. Canvas silently ignores an unparseable
 * `fillStyle`, which would leave the word being spoken painted in the base
 * colour, the one word that must stand out. Rebuild it as a real canvas
 * gradient with a hard stop at the sweep position.
 */
export function resolveFill(
  ctx: Ctx,
  requested: string,
  segmentWidth: number,
  highlightColor: string,
  baseColor: string,
): string | CanvasGradient {
  if (!requested.startsWith("linear-gradient")) return requested;

  const stop = /(\d+(?:\.\d+)?)%/.exec(requested);
  const progress = stop ? Math.min(100, Math.max(0, Number(stop[1]))) / 100 : 0;

  try {
    const gradient = ctx.createLinearGradient(
      -segmentWidth / 2,
      0,
      segmentWidth / 2,
      0,
    );
    gradient.addColorStop(0, highlightColor);
    gradient.addColorStop(progress, highlightColor);
    gradient.addColorStop(Math.min(1, progress + 0.001), baseColor);
    gradient.addColorStop(1, baseColor);
    return gradient;
  } catch {
    // A zero-width segment makes createLinearGradient throw; the flat
    // highlight is a fine stand-in for a word too small to sweep.
    return highlightColor;
  }
}

/** Draws the caption's plate behind a line of text. */
function paintBackground(
  ctx: Ctx,
  style: TextStyle,
  lineWidth: number,
  y: number,
  lineHeight: number,
): void {
  if (!style.backgroundColor) return;

  const padX = style.fontSize * 0.3;
  const padY = style.fontSize * 0.12;

  ctx.save();
  ctx.fillStyle = style.backgroundColor;
  ctx.fillRect(
    -lineWidth / 2 - padX,
    y - lineHeight / 2 - padY,
    lineWidth + padX * 2,
    lineHeight + padY * 2,
  );
  ctx.restore();
}

function applyStyle(ctx: Ctx, style: TextStyle): void {
  ctx.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize}px "${style.fontFamily}", sans-serif`;
  ctx.textBaseline = "middle";
  ctx.textAlign = "left";
}

/**
 * Paints one animated caption frame.
 *
 * Returns false when the caption is not on screen at this time, so the caller
 * can skip the rest of its own drawing.
 */
export function renderCaptionFrame(
  ctx: Ctx,
  data: CaptionClipData,
  text: string,
  style: TextStyle,
  clipDuration: number,
  localTime: number,
  maxWidth: number,
): boolean {
  // renderAnimatedCaption works in absolute subtitle time; our word timings are
  // clip-relative, so the synthetic subtitle simply starts at zero.
  //
  // The highlight colour is handed over through `style.highlightColor` (the
  // channel the animation renderer already reads) rather than being patched
  // onto the segments afterwards, which would fight its own default.
  const subtitle: Subtitle = {
    id: "caption-clip",
    text,
    startTime: 0,
    endTime: clipDuration,
    words: data.words as SubtitleWord[],
    animationStyle: data.animationStyle,
    style: {
      fontFamily: style.fontFamily,
      fontSize: style.fontSize,
      color: style.color,
      backgroundColor: style.backgroundColor ?? "transparent",
      position: "bottom",
      highlightColor: data.highlightColor ?? style.color,
    },
  };

  const frame = renderAnimatedCaption(subtitle, localTime);
  if (!frame.visible || frame.segments.length === 0) return false;

  applyStyle(ctx, style);

  const lines = layoutLines(ctx, frame.segments, maxWidth);
  const lineHeight = style.fontSize * style.lineHeight;
  const totalHeight = lines.length * lineHeight;
  const spaceWidth = ctx.measureText(" ").width;

  let y = -totalHeight / 2 + lineHeight / 2;

  for (const line of lines) {
    // Painted per line, before the words, so a preset built around a plate
    // ("Soft", "Typewriter") keeps its plate instead of losing contrast.
    paintBackground(ctx, style, line.width, y, lineHeight);

    let x =
      style.textAlign === "left"
        ? -maxWidth / 2
        : style.textAlign === "right"
          ? maxWidth / 2 - line.width
          : -line.width / 2;

    for (const segment of line.segments) {
      const segmentWidth = ctx.measureText(segment.text).width;

      if (segment.style !== "hidden" && segment.opacity > 0) {
        ctx.save();

        // Scale each word about its own centre so a bouncing word grows in
        // place instead of sliding right.
        const centerX = x + segmentWidth / 2;
        const centerY = y + segment.offsetY;
        ctx.translate(centerX, centerY);
        ctx.scale(segment.scale, segment.scale);
        ctx.globalAlpha *= segment.opacity;

        const requested =
          segment.color ??
          (segment.style === "active" || segment.style === "highlighted"
            ? (data.highlightColor ?? style.color)
            : style.color);

        const fill = resolveFill(
          ctx,
          requested,
          segmentWidth,
          data.highlightColor ?? style.color,
          style.color,
        );

        if (style.shadowColor && style.shadowBlur) {
          ctx.shadowColor = style.shadowColor;
          ctx.shadowBlur = style.shadowBlur;
          ctx.shadowOffsetX = style.shadowOffsetX ?? 0;
          ctx.shadowOffsetY = style.shadowOffsetY ?? 0;
        }

        if (style.strokeColor && style.strokeWidth) {
          ctx.lineWidth = style.strokeWidth;
          ctx.strokeStyle = style.strokeColor;
          ctx.lineJoin = "round";
          ctx.miterLimit = 2;
          ctx.strokeText(segment.text, -segmentWidth / 2, 0);
        }

        ctx.fillStyle = fill;
        ctx.fillText(segment.text, -segmentWidth / 2, 0);

        ctx.restore();
      }

      x += segmentWidth + spaceWidth;
    }

    y += lineHeight;
  }

  return true;
}
